import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { apiFetch } from '@/lib/operator-api';
import {
  Search,
  Plus,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle,
  Pause,
  Trash2,
  Radio,
  Users,
  Eye,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  Megaphone,
  CalendarClock,
  Filter,
  Mail,
  MessageSquare,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

// ============================================================================
// Types
// ============================================================================

interface Broadcast {
  id: string;
  name: string;
  status: BroadcastStatus;
  templateId?: string;
  templateName?: string;
  segmentId?: string;
  segmentName?: string | null;
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  readCount: number;
  failedCount: number;
  scheduledAt: string | null;
  sendStartedAt: string | null;
  sendCompletedAt: string | null;
  createdAt: string;
}

interface BroadcastStats {
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  readCount: number;
  failedCount: number;
  respondedCount: number;
  deliveryRate: number;
  readRate: number;
  responseRate: number;
}

interface AudienceSegment {
  id: string;
  name: string;
  description?: string;
  type: 'preset' | 'custom' | 'PRESET' | 'CUSTOM';
  audienceSize?: number;
  estimatedSize?: number;
  filters?: Record<string, unknown>;
}

interface TemplateOption {
  id: string;
  slug: string;
  name: string;
}

type BroadcastStatus = 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'PAUSED' | 'CANCELLED' | 'FAILED';

// ============================================================================
// Constants & Helpers
// ============================================================================

const BROADCAST_STATUSES: BroadcastStatus[] = [
  'DRAFT', 'SCHEDULED', 'SENDING', 'SENT', 'PAUSED', 'CANCELLED', 'FAILED',
];

function getStatusBadge(status: BroadcastStatus): {
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  className: string;
  icon: React.ReactNode;
} {
  switch (status) {
    case 'DRAFT':
      return {
        variant: 'secondary',
        className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
        icon: <Mail className="h-3 w-3" />,
      };
    case 'SCHEDULED':
      return {
        variant: 'default',
        className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        icon: <CalendarClock className="h-3 w-3" />,
      };
    case 'SENDING':
      return {
        variant: 'default',
        className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
        icon: <Radio className="h-3 w-3 animate-pulse" />,
      };
    case 'SENT':
      return {
        variant: 'default',
        className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        icon: <CheckCircle className="h-3 w-3" />,
      };
    case 'PAUSED':
      return {
        variant: 'default',
        className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
        icon: <Pause className="h-3 w-3" />,
      };
    case 'CANCELLED':
      return {
        variant: 'destructive',
        className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        icon: <XCircle className="h-3 w-3" />,
      };
    case 'FAILED':
      return {
        variant: 'destructive',
        className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        icon: <AlertCircle className="h-3 w-3" />,
      };
    default:
      return { variant: 'outline', className: '', icon: null };
  }
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDateTime(dateStr);
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

// ============================================================================
// API Helpers
// ============================================================================

const broadcastApi = {
  async list(businessId: string, params?: { status?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams({ businessId });
    if (params?.status && params.status !== 'ALL') searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const res = await apiFetch(`/api/operator/broadcasts?${searchParams}`);
    if (!res.ok) throw new Error('Failed to load broadcasts');
    return res.json();
  },

  async get(id: string) {
    const res = await apiFetch(`/api/operator/broadcasts/${id}`);
    if (!res.ok) throw new Error('Failed to load broadcast');
    return res.json();
  },

  async create(body: { businessId: string; name: string; templateId: string; segmentId: string }) {
    const res = await apiFetch('/api/operator/broadcasts', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Failed to create broadcast');
    return res.json();
  },

  async delete(id: string) {
    const res = await apiFetch(`/api/operator/broadcasts/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete broadcast');
    return res.json();
  },

  async send(id: string) {
    const res = await apiFetch(`/api/operator/broadcasts/${id}/send`, { method: 'POST' });
    if (!res.ok) throw new Error('Failed to send broadcast');
    return res.json();
  },

  async execute(id: string) {
    const res = await apiFetch(`/api/operator/broadcasts/${id}/execute`, { method: 'POST' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      if (data.error === 'WHATSAPP_NOT_CONFIGURED') {
        throw new Error('WHATSAPP_NOT_CONFIGURED');
      }
      throw new Error(data.message || data.error || 'Failed to execute broadcast');
    }
    return res.json();
  },

  async executionStatus(id: string, detailed: boolean = false) {
    const params = detailed ? '?detailed=true' : '';
    const res = await apiFetch(`/api/operator/broadcasts/${id}/status${params}`);
    if (!res.ok) throw new Error('Failed to load execution status');
    const data = await res.json();
    return data.data || data;
  },

  async schedule(id: string, scheduledAt: string) {
    const res = await apiFetch(`/api/operator/broadcasts/${id}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ scheduledAt }),
    });
    if (!res.ok) throw new Error('Failed to schedule broadcast');
    return res.json();
  },

  async pause(id: string) {
    const res = await apiFetch(`/api/operator/broadcasts/${id}/pause`, { method: 'POST' });
    if (!res.ok) throw new Error('Failed to pause broadcast');
    return res.json();
  },

  async cancel(id: string) {
    const res = await apiFetch(`/api/operator/broadcasts/${id}/cancel`, { method: 'POST' });
    if (!res.ok) throw new Error('Failed to cancel broadcast');
    return res.json();
  },

  async stats(id: string): Promise<BroadcastStats> {
    const res = await apiFetch(`/api/operator/broadcasts/${id}/stats`);
    if (!res.ok) throw new Error('Failed to load stats');
    const data = await res.json();
    return data.data || data;
  },

  async getSegments(businessId: string): Promise<AudienceSegment[]> {
    const res = await apiFetch(`/api/operator/broadcasts/segments?businessId=${encodeURIComponent(businessId)}`);
    if (!res.ok) throw new Error('Failed to load segments');
    const data = await res.json();
    return data.data || data.segments || [];
  },

  async getPresetSegments(businessId: string): Promise<AudienceSegment[]> {
    const res = await apiFetch(`/api/operator/broadcasts/segments/presets?businessId=${encodeURIComponent(businessId)}`);
    if (!res.ok) throw new Error('Failed to load presets');
    const data = await res.json();
    return data.data || data.segments || [];
  },

  async createSegment(body: { businessId: string; name: string; description?: string; filters?: Record<string, unknown> }) {
    const res = await apiFetch('/api/operator/broadcasts/segments', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Failed to create segment');
    return res.json();
  },

  async computeSegmentSize(segmentId: string) {
    const res = await apiFetch(`/api/operator/broadcasts/segments/${segmentId}/compute`, { method: 'POST' });
    if (!res.ok) throw new Error('Failed to compute audience');
    const data = await res.json();
    const result = data.data || data;
    return { audienceSize: result.count ?? result.audienceSize ?? result.size ?? null };
  },

  async getTemplates(businessId: string): Promise<TemplateOption[]> {
    const res = await apiFetch(`/api/operator/templates?businessId=${encodeURIComponent(businessId)}`);
    if (!res.ok) throw new Error('Failed to load templates');
    const data = await res.json();
    const raw = data.data || data;
    const templates = raw.templates || [];
    return templates.map((t: { slug: string; template?: { id?: string } }) => ({
      id: t.template?.id || t.slug,
      slug: t.slug,
      name: t.slug.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
    }));
  },
};

// ============================================================================
// Create Campaign Dialog
// ============================================================================

function CreateCampaignDialog({
  open,
  onOpenChange,
  businessId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedSegmentId, setSelectedSegmentId] = useState('');
  const [scheduleType, setScheduleType] = useState<'now' | 'later'>('now');
  const [scheduledAt, setScheduledAt] = useState('');

  // Available options
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [segments, setSegments] = useState<AudienceSegment[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [audienceSize, setAudienceSize] = useState<number | null>(null);
  const [computingAudience, setComputingAudience] = useState(false);

  // Load templates and segments when dialog opens
  useEffect(() => {
    if (!open || !businessId) return;
    const controller = new AbortController();
    setLoadingOptions(true);
    setError(null);
    setStep(0);
    setName('');
    setSelectedTemplateId('');
    setSelectedSegmentId('');
    setScheduleType('now');
    setScheduledAt('');
    setAudienceSize(null);

    Promise.all([
      broadcastApi.getTemplates(businessId),
      broadcastApi.getSegments(businessId).catch(() => []),
      broadcastApi.getPresetSegments(businessId).catch(() => []),
    ])
      .then(([tmpl, segs, presets]) => {
        if (controller.signal.aborted) return;
        setTemplates(tmpl);
        const allSegments = [
          ...presets.map((p: AudienceSegment) => ({ ...p, type: 'preset' as const, audienceSize: p.audienceSize ?? p.estimatedSize })),
          ...segs.map((s: AudienceSegment) => ({ ...s, type: s.type || ('custom' as const), audienceSize: s.audienceSize ?? s.estimatedSize })),
        ];
        // Deduplicate by id
        const seen = new Set<string>();
        const unique = allSegments.filter((s) => {
          if (seen.has(s.id)) return false;
          seen.add(s.id);
          return true;
        });
        setSegments(unique);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load options');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingOptions(false);
      });
    return () => controller.abort();
  }, [open, businessId]);

  // Compute audience size when segment changes
  useEffect(() => {
    if (!selectedSegmentId) {
      setAudienceSize(null);
      return;
    }
    const controller = new AbortController();
    setComputingAudience(true);
    broadcastApi
      .computeSegmentSize(selectedSegmentId)
      .then((data) => {
        if (controller.signal.aborted) return;
        setAudienceSize(data.audienceSize ?? null);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setAudienceSize(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setComputingAudience(false);
      });
    return () => controller.abort();
  }, [selectedSegmentId]);

  const steps = [
    t('broadcasts.wizard.nameStep'),
    t('broadcasts.wizard.templateStep'),
    t('broadcasts.wizard.audienceStep'),
    t('broadcasts.wizard.reviewStep'),
  ];

  const canProceed = () => {
    switch (step) {
      case 0: return name.trim().length > 0;
      case 1: return selectedTemplateId !== '';
      case 2: return selectedSegmentId !== '';
      case 3: return true;
      default: return false;
    }
  };

  const handleCreate = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const body: Parameters<typeof broadcastApi.create>[0] = {
        businessId,
        name: name.trim(),
        templateId: selectedTemplateId,
        segmentId: selectedSegmentId,
      };
      const result = await broadcastApi.create(body);
      const broadcastId = result.id || result.data?.id;

      // If send now, trigger send immediately
      if (scheduleType === 'now' && broadcastId) {
        await broadcastApi.send(broadcastId);
      } else if (scheduleType === 'later' && scheduledAt && broadcastId) {
        await broadcastApi.schedule(broadcastId, new Date(scheduledAt).toISOString());
      }

      onCreated();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
    } finally {
      setSaving(false);
    }
  }, [businessId, name, selectedTemplateId, selectedSegmentId, scheduleType, scheduledAt, onCreated, onOpenChange]);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
  const selectedSegment = segments.find((s) => s.id === selectedSegmentId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('broadcasts.createCampaign')}</DialogTitle>
          <DialogDescription>{t('broadcasts.createCampaignDesc')}</DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-4">
          {steps.map((label, i) => (
            <div key={i} className="flex items-center gap-2 flex-1">
              <div
                className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium transition-colors ${
                  i < step
                    ? 'bg-primary text-primary-foreground'
                    : i === step
                      ? 'bg-primary text-primary-foreground ring-2 ring-primary/30'
                      : 'bg-muted text-muted-foreground'
                }`}
              >
                {i < step ? <CheckCircle className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`text-xs hidden sm:inline ${i === step ? 'font-medium' : 'text-muted-foreground'}`}>
                {label}
              </span>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-px ${i < step ? 'bg-primary' : 'bg-muted'}`} />
              )}
            </div>
          ))}
        </div>

        {loadingOptions ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <>
            {/* Step 0: Name */}
            {step === 0 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="campaign-name">{t('broadcasts.campaignName')}</Label>
                  <Input
                    id="campaign-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('broadcasts.campaignNamePlaceholder')}
                    autoFocus
                  />
                </div>
              </div>
            )}

            {/* Step 1: Template */}
            {step === 1 && (
              <div className="space-y-4">
                <Label>{t('broadcasts.selectTemplate')}</Label>
                {templates.length === 0 ? (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center py-8">
                      <MessageSquare className="h-8 w-8 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">{t('broadcasts.noTemplates')}</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[300px] overflow-y-auto">
                    {templates.map((tmpl) => (
                      <Card
                        key={tmpl.id}
                        className={`cursor-pointer transition-all hover:shadow-md ${
                          selectedTemplateId === tmpl.id
                            ? 'border-primary ring-2 ring-primary/20'
                            : 'hover:border-primary/50'
                        }`}
                        onClick={() => setSelectedTemplateId(tmpl.id)}
                      >
                        <CardContent className="flex items-center gap-3 py-3 px-4">
                          <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium truncate">{tmpl.name}</span>
                          {selectedTemplateId === tmpl.id && (
                            <CheckCircle className="h-4 w-4 text-primary ml-auto shrink-0" />
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Audience */}
            {step === 2 && (
              <div className="space-y-4">
                <Label>{t('broadcasts.selectAudience')}</Label>
                {segments.length === 0 ? (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center py-8">
                      <Users className="h-8 w-8 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">{t('broadcasts.noSegments')}</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {segments.map((seg) => (
                      <Card
                        key={seg.id}
                        className={`cursor-pointer transition-all hover:shadow-md ${
                          selectedSegmentId === seg.id
                            ? 'border-primary ring-2 ring-primary/20'
                            : 'hover:border-primary/50'
                        }`}
                        onClick={() => setSelectedSegmentId(seg.id)}
                      >
                        <CardContent className="flex items-center gap-3 py-3 px-4">
                          <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium truncate">{seg.name}</span>
                              {seg.type === 'preset' && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                  {t('broadcasts.preset')}
                                </Badge>
                              )}
                            </div>
                            {seg.description && (
                              <p className="text-xs text-muted-foreground truncate">{seg.description}</p>
                            )}
                          </div>
                          {selectedSegmentId === seg.id && (
                            <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {selectedSegmentId && (
                  <Card className="bg-muted/50">
                    <CardContent className="flex items-center gap-3 py-3 px-4">
                      <Users className="h-4 w-4 text-primary" />
                      <span className="text-sm">
                        {t('broadcasts.estimatedAudience')}:{' '}
                        {computingAudience ? (
                          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent align-middle" />
                        ) : audienceSize !== null ? (
                          <strong>{audienceSize.toLocaleString()}</strong>
                        ) : (
                          '--'
                        )}
                        {' '}{t('broadcasts.recipients')}
                      </span>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Step 3: Review & Schedule */}
            {step === 3 && (
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">{t('broadcasts.campaignSummary')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('broadcasts.name')}</span>
                      <span className="font-medium">{name}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('broadcasts.template')}</span>
                      <span className="font-medium">{selectedTemplate?.name || '--'}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('broadcasts.audience')}</span>
                      <span className="font-medium">
                        {selectedSegment?.name || '--'}
                        {audienceSize !== null && ` (${audienceSize.toLocaleString()})`}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-3">
                  <Label>{t('broadcasts.whenToSend')}</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Card
                      className={`cursor-pointer transition-all ${
                        scheduleType === 'now'
                          ? 'border-primary ring-2 ring-primary/20'
                          : 'hover:border-primary/50'
                      }`}
                      onClick={() => setScheduleType('now')}
                    >
                      <CardContent className="flex items-center gap-3 py-3 px-4">
                        <Send className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">{t('broadcasts.sendNow')}</span>
                      </CardContent>
                    </Card>
                    <Card
                      className={`cursor-pointer transition-all ${
                        scheduleType === 'later'
                          ? 'border-primary ring-2 ring-primary/20'
                          : 'hover:border-primary/50'
                      }`}
                      onClick={() => setScheduleType('later')}
                    >
                      <CardContent className="flex items-center gap-3 py-3 px-4">
                        <CalendarClock className="h-4 w-4 text-blue-500" />
                        <span className="text-sm font-medium">{t('broadcasts.scheduleLater')}</span>
                      </CardContent>
                    </Card>
                  </div>

                  {scheduleType === 'later' && (
                    <div className="space-y-2">
                      <Label htmlFor="schedule-date">{t('broadcasts.scheduleDate')}</Label>
                      <Input
                        id="schedule-date"
                        type="datetime-local"
                        value={scheduledAt}
                        onChange={(e) => setScheduledAt(e.target.value)}
                        min={new Date().toISOString().slice(0, 16)}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 px-4 py-3">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {step > 0 && (
            <Button variant="outline" onClick={() => setStep(step - 1)} disabled={saving} className="sm:mr-auto">
              <ChevronLeft className="mr-1 h-4 w-4" />
              {t('broadcasts.back')}
            </Button>
          )}
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canProceed()}>
              {t('broadcasts.next')}
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleCreate} disabled={saving || (scheduleType === 'later' && !scheduledAt)}>
              {saving ? (
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : scheduleType === 'now' ? (
                <Send className="mr-2 h-4 w-4" />
              ) : (
                <CalendarClock className="mr-2 h-4 w-4" />
              )}
              {saving
                ? t('broadcasts.creating')
                : scheduleType === 'now'
                  ? t('broadcasts.sendCampaign')
                  : t('broadcasts.scheduleCampaign')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Campaign Detail Sheet
// ============================================================================

interface ExecutionResult {
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
}

function CampaignDetailSheet({
  open,
  onOpenChange,
  broadcast,
  onAction,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  broadcast: Broadcast | null;
  onAction: () => void;
}) {
  const { t } = useTranslation();
  const [stats, setStats] = useState<BroadcastStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<ExecutionResult | null>(null);
  const [executionError, setExecutionError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !broadcast) {
      setStats(null);
      setExecutionResult(null);
      setExecutionError(null);
      return;
    }
    const controller = new AbortController();
    setLoadingStats(true);
    broadcastApi
      .stats(broadcast.id)
      .then((data) => {
        if (controller.signal.aborted) return;
        setStats(data);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setStats(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingStats(false);
      });
    return () => controller.abort();
  }, [open, broadcast]);

  const handleExecute = useCallback(async () => {
    if (!broadcast) return;
    setConfirmSendOpen(false);
    setExecuting(true);
    setExecutionError(null);
    setExecutionResult(null);
    try {
      const res = await broadcastApi.execute(broadcast.id);
      const data = res.data || res;
      setExecutionResult({
        totalRecipients: data.totalRecipients,
        sentCount: data.sentCount,
        failedCount: data.failedCount,
      });
      onAction();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to execute broadcast';
      if (msg === 'WHATSAPP_NOT_CONFIGURED') {
        setExecutionError(t('broadcasts.noWhatsApp'));
      } else {
        setExecutionError(msg);
      }
    } finally {
      setExecuting(false);
    }
  }, [broadcast, onAction, t]);

  const handleAction = useCallback(
    async (action: 'send' | 'pause' | 'cancel' | 'delete') => {
      if (!broadcast) return;
      setActionLoading(action);
      try {
        switch (action) {
          case 'send':
            // Open confirm dialog for execution instead
            setConfirmSendOpen(true);
            setActionLoading(null);
            return;
          case 'pause':
            await broadcastApi.pause(broadcast.id);
            break;
          case 'cancel':
            await broadcastApi.cancel(broadcast.id);
            break;
          case 'delete':
            await broadcastApi.delete(broadcast.id);
            break;
        }
        onAction();
        onOpenChange(false);
      } catch {
        // Error handling is silent -- the list will refresh
      } finally {
        setActionLoading(null);
      }
    },
    [broadcast, onAction, onOpenChange],
  );

  if (!broadcast) return null;

  const statusBadge = getStatusBadge(broadcast.status);
  const deliveryRate = stats ? stats.deliveryRate : 0;
  const readRate = stats ? stats.readRate : 0;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5" />
              {broadcast.name}
            </SheetTitle>
            <SheetDescription>
              {t('broadcasts.createdAt')}: {formatDateTime(broadcast.createdAt)}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Status */}
            <div className="flex items-center gap-3">
              <Badge className={`flex items-center gap-1 ${statusBadge.className}`}>
                {statusBadge.icon}
                {t(`broadcasts.status.${broadcast.status}`)}
              </Badge>
              {broadcast.scheduledAt && broadcast.status === 'SCHEDULED' && (
                <span className="text-sm text-muted-foreground">
                  {formatDateTime(broadcast.scheduledAt)}
                </span>
              )}
            </div>

            {/* Execution result banner */}
            {executionResult && (
              <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/20">
                <CardContent className="pt-4 space-y-2">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">{t('broadcasts.sent')}</span>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span>{t('broadcasts.delivered')}: <strong>{executionResult.sentCount}</strong></span>
                    {executionResult.failedCount > 0 && (
                      <span className="text-red-600">{t('broadcasts.failed')}: <strong>{executionResult.failedCount}</strong></span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Execution error banner */}
            {executionError && (
              <div className="rounded-md bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 px-4 py-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <p className="text-sm text-red-600 dark:text-red-400">{executionError}</p>
                </div>
              </div>
            )}

            {/* Executing progress */}
            {executing && (
              <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20">
                <CardContent className="flex items-center gap-3 pt-4">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-amber-600 border-t-transparent" />
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
                    {t('broadcasts.executing')}
                  </span>
                </CardContent>
              </Card>
            )}

            {/* Campaign Info */}
            <Card>
              <CardContent className="pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('broadcasts.template')}</span>
                  <span className="font-medium">{broadcast.templateName || broadcast.templateId}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('broadcasts.audience')}</span>
                  <span className="font-medium">{broadcast.segmentName || broadcast.segmentId}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('broadcasts.recipientCount')}</span>
                  <span className="font-medium">{broadcast.totalRecipients.toLocaleString()}</span>
                </div>
                {broadcast.sendStartedAt && (
                  <>
                    <Separator />
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('broadcasts.sentAt')}</span>
                      <span className="font-medium">{formatDateTime(broadcast.sendStartedAt)}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Delivery Stats */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium">{t('broadcasts.deliveryStats')}</h3>
              {loadingStats ? (
                <div className="flex items-center justify-center h-24">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                </div>
              ) : stats ? (
                <div className="grid grid-cols-2 gap-3">
                  <Card>
                    <CardContent className="pt-3 pb-3 text-center">
                      <div className="text-2xl font-bold">{stats.sentCount.toLocaleString()}</div>
                      <p className="text-xs text-muted-foreground">{t('broadcasts.statsSent')}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-3 pb-3 text-center">
                      <div className="text-2xl font-bold text-green-600">{stats.deliveredCount.toLocaleString()}</div>
                      <p className="text-xs text-muted-foreground">{t('broadcasts.statsDelivered')}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-3 pb-3 text-center">
                      <div className="text-2xl font-bold text-blue-600">{stats.readCount.toLocaleString()}</div>
                      <p className="text-xs text-muted-foreground">{t('broadcasts.statsRead')}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-3 pb-3 text-center">
                      <div className="text-2xl font-bold text-red-600">{stats.failedCount.toLocaleString()}</div>
                      <p className="text-xs text-muted-foreground">{t('broadcasts.statsFailed')}</p>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card>
                  <CardContent className="flex items-center justify-center py-6">
                    <p className="text-sm text-muted-foreground">{t('broadcasts.noStatsYet')}</p>
                  </CardContent>
                </Card>
              )}

              {stats && stats.totalRecipients > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{t('broadcasts.deliveryRate')}</span>
                    <span className="font-medium">{deliveryRate.toFixed(1)}%</span>
                  </div>
                  <Progress value={deliveryRate} className="h-2" />
                  <div className="flex justify-between text-xs mt-2">
                    <span className="text-muted-foreground">{t('broadcasts.readRate')}</span>
                    <span className="font-medium">{readRate.toFixed(1)}%</span>
                  </div>
                  <Progress value={readRate} className="h-2" />
                </div>
              )}
            </div>

            <Separator />

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              {(broadcast.status === 'DRAFT' || broadcast.status === 'PAUSED') && (
                <Button
                  onClick={() => handleAction('send')}
                  disabled={actionLoading !== null || executing}
                  className="flex-1"
                >
                  {executing ? (
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  {executing ? t('broadcasts.executing') : t('broadcasts.execute')}
                </Button>
              )}
              {(broadcast.status === 'SENDING' || broadcast.status === 'SCHEDULED') && (
                <Button
                  variant="outline"
                  onClick={() => handleAction('pause')}
                  disabled={actionLoading !== null}
                  className="flex-1"
                >
                  {actionLoading === 'pause' ? (
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <Pause className="mr-2 h-4 w-4" />
                  )}
                  {t('broadcasts.pause')}
                </Button>
              )}
              {broadcast.status !== 'CANCELLED' && broadcast.status !== 'SENT' && broadcast.status !== 'FAILED' && (
                <Button
                  variant="outline"
                  onClick={() => handleAction('cancel')}
                  disabled={actionLoading !== null}
                >
                  {actionLoading === 'cancel' ? (
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <XCircle className="mr-2 h-4 w-4" />
                  )}
                  {t('broadcasts.cancel')}
                </Button>
              )}
              {broadcast.status === 'DRAFT' && (
                <Button
                  variant="destructive"
                  onClick={() => handleAction('delete')}
                  disabled={actionLoading !== null}
                >
                  {actionLoading === 'delete' ? (
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  {t('broadcasts.delete')}
                </Button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Confirm Send Dialog */}
      <Dialog open={confirmSendOpen} onOpenChange={setConfirmSendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('broadcasts.execute')}</DialogTitle>
            <DialogDescription>
              {t('broadcasts.confirmSend', { count: broadcast.totalRecipients || 0 })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSendOpen(false)}>
              {t('broadcasts.cancelAction')}
            </Button>
            <Button onClick={handleExecute}>
              <Send className="mr-2 h-4 w-4" />
              {t('broadcasts.execute')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================================
// Audience Segments Tab
// ============================================================================

function AudienceSegmentsTab({ businessId }: { businessId: string }) {
  const { t } = useTranslation();
  const [segments, setSegments] = useState<AudienceSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchSegments = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const [segs, presets] = await Promise.all([
        broadcastApi.getSegments(businessId).catch(() => []),
        broadcastApi.getPresetSegments(businessId).catch(() => []),
      ]);
      if (signal?.aborted) return;
      const all = [
        ...presets.map((p: AudienceSegment) => ({ ...p, type: 'preset' as const, audienceSize: p.audienceSize ?? p.estimatedSize })),
        ...segs.map((s: AudienceSegment) => ({ ...s, type: s.type || ('custom' as const), audienceSize: s.audienceSize ?? s.estimatedSize })),
      ];
      const seen = new Set<string>();
      setSegments(all.filter((s) => {
        if (seen.has(s.id)) return false;
        seen.add(s.id);
        return true;
      }));
    } catch (err) {
      if (signal?.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load segments');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    const controller = new AbortController();
    fetchSegments(controller.signal);
    return () => controller.abort();
  }, [fetchSegments]);

  const handleCreate = useCallback(async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await broadcastApi.createSegment({
        businessId,
        name: newName.trim(),
        description: newDescription.trim() || undefined,
      });
      setShowCreate(false);
      setNewName('');
      setNewDescription('');
      fetchSegments();
    } catch {
      // Silent -- refresh will show current state
    } finally {
      setCreating(false);
    }
  }, [businessId, newName, newDescription, fetchSegments]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('broadcasts.segmentsDesc')}</p>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="mr-1 h-4 w-4" />
          {t('broadcasts.createSegment')}
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive p-4">
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      {segments.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t('broadcasts.noSegments')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {segments.map((seg) => (
            <Card key={seg.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-sm font-medium">{seg.name}</CardTitle>
                  <Badge variant={seg.type === 'preset' ? 'secondary' : 'outline'} className="text-[10px]">
                    {seg.type === 'preset' ? t('broadcasts.preset') : t('broadcasts.custom')}
                  </Badge>
                </div>
                {seg.description && (
                  <CardDescription className="text-xs">{seg.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Users className="h-3.5 w-3.5" />
                  <span>
                    {seg.audienceSize !== undefined
                      ? `${seg.audienceSize.toLocaleString()} ${t('broadcasts.recipients')}`
                      : t('broadcasts.sizeUnknown')}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Segment Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('broadcasts.createSegment')}</DialogTitle>
            <DialogDescription>{t('broadcasts.createSegmentDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="seg-name">{t('broadcasts.segmentName')}</Label>
              <Input
                id="seg-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t('broadcasts.segmentNamePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="seg-desc">{t('broadcasts.segmentDescription')}</Label>
              <Textarea
                id="seg-desc"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder={t('broadcasts.segmentDescPlaceholder')}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              {t('broadcasts.cancelAction')}
            </Button>
            <Button onClick={handleCreate} disabled={creating || !newName.trim()}>
              {creating ? (
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              {t('broadcasts.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Main Broadcasts Page
// ============================================================================

function BroadcastsPage() {
  const { t } = useTranslation();
  const { operator, isLoading: authLoading, selectedBusinessId, selectedBusiness } = useOperator();

  // State
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Dialogs
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState<Broadcast | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Summary stats
  const summary = useMemo(() => {
    const total = broadcasts.length;
    const draft = broadcasts.filter((b) => b.status === 'DRAFT').length;
    const scheduled = broadcasts.filter((b) => b.status === 'SCHEDULED').length;
    const sending = broadcasts.filter((b) => b.status === 'SENDING').length;
    const sent = broadcasts.filter((b) => b.status === 'SENT').length;
    return { total, draft, scheduled, sending, sent };
  }, [broadcasts]);

  const fetchBroadcasts = useCallback(async (signal?: AbortSignal) => {
    if (!selectedBusinessId || !operator) return;
    setLoading(true);
    setFetchError(null);

    try {
      const data = await broadcastApi.list(selectedBusinessId, {
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        page: currentPage,
        limit: 20,
      });
      if (signal?.aborted) return;
      const items = data.data || data.broadcasts || [];
      setBroadcasts(items);
      setTotalPages(data.totalPages || data.pagination?.totalPages || 1);
    } catch (err) {
      if (signal?.aborted) return;
      setFetchError(err instanceof Error ? err.message : 'Failed to load broadcasts');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [selectedBusinessId, operator, statusFilter, currentPage]);

  useEffect(() => {
    const controller = new AbortController();
    fetchBroadcasts(controller.signal);
    return () => controller.abort();
  }, [fetchBroadcasts]);

  // Filter by search locally
  const filteredBroadcasts = useMemo(() => {
    if (!searchQuery) return broadcasts;
    const q = searchQuery.toLowerCase();
    return broadcasts.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        (b.templateName && b.templateName.toLowerCase().includes(q)) ||
        (b.segmentName && b.segmentName.toLowerCase().includes(q)),
    );
  }, [broadcasts, searchQuery]);

  const handleViewDetail = useCallback((broadcast: Broadcast) => {
    setSelectedBroadcast(broadcast);
    setDetailOpen(true);
  }, []);

  // Loading state
  if (authLoading) {
    return <LoadingSpinner />;
  }

  if (!operator) return null;

  // No business selected
  if (!selectedBusinessId) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Megaphone className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              {t('broadcasts.selectBusiness')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-primary" />
            {t('broadcasts.title')}
          </h1>
          <p className="text-muted-foreground">
            {selectedBusiness?.name && `${selectedBusiness.name} - `}
            {t('broadcasts.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchBroadcasts()}>
            <RefreshCw className="mr-1 h-4 w-4" />
            {t('broadcasts.refresh')}
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            {t('broadcasts.newCampaign')}
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {fetchError && (
        <div className="rounded-lg border border-destructive p-4">
          <p className="text-destructive text-sm">{fetchError}</p>
        </div>
      )}

      {/* Tabs: Campaigns / Segments */}
      <Tabs defaultValue="campaigns">
        <TabsList>
          <TabsTrigger value="campaigns">
            <Radio className="mr-1.5 h-4 w-4" />
            {t('broadcasts.campaigns')}
          </TabsTrigger>
          <TabsTrigger value="segments">
            <Users className="mr-1.5 h-4 w-4" />
            {t('broadcasts.segments')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns" className="space-y-4 mt-4">
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('broadcasts.totalCampaigns')}</CardTitle>
                <Megaphone className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.total}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('broadcasts.drafts')}</CardTitle>
                <Mail className="h-4 w-4 text-gray-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.draft}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('broadcasts.scheduled')}</CardTitle>
                <CalendarClock className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.scheduled}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('broadcasts.sentCampaigns')}</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.sent}</div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t('broadcasts.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
                  <SelectTrigger className="w-[180px]">
                    <Filter className="mr-2 h-4 w-4" />
                    <SelectValue placeholder={t('broadcasts.filterByStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">{t('broadcasts.allStatuses')}</SelectItem>
                    {BROADCAST_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {t(`broadcasts.status.${status}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Campaigns Table */}
          {loading ? (
            <LoadingSpinner />
          ) : filteredBroadcasts.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Megaphone className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  {searchQuery || statusFilter !== 'ALL'
                    ? t('broadcasts.noMatchingCampaigns')
                    : t('broadcasts.noCampaigns')}
                </p>
                {!searchQuery && statusFilter === 'ALL' && (
                  <Button onClick={() => setCreateOpen(true)}>
                    <Plus className="mr-1 h-4 w-4" />
                    {t('broadcasts.createFirst')}
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('broadcasts.campaignName')}</TableHead>
                        <TableHead>{t('broadcasts.statusLabel')}</TableHead>
                        <TableHead>{t('broadcasts.audience')}</TableHead>
                        <TableHead>{t('broadcasts.recipientCount')}</TableHead>
                        <TableHead>{t('broadcasts.scheduledSent')}</TableHead>
                        <TableHead className="text-right">{t('broadcasts.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredBroadcasts.map((broadcast) => {
                        const statusBadge = getStatusBadge(broadcast.status);
                        return (
                          <TableRow key={broadcast.id} className="cursor-pointer hover:bg-muted/50" onClick={() => handleViewDetail(broadcast)}>
                            <TableCell>
                              <div>
                                <div className="font-medium">{broadcast.name}</div>
                                <div className="text-xs text-muted-foreground">
                                  {broadcast.templateName || broadcast.templateId}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={`flex items-center gap-1 w-fit ${statusBadge.className}`}>
                                {statusBadge.icon}
                                {t(`broadcasts.status.${broadcast.status}`)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">{broadcast.segmentName || broadcast.segmentId}</span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                <span>{broadcast.totalRecipients.toLocaleString()}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-muted-foreground">
                                {broadcast.sendStartedAt
                                  ? formatRelativeTime(broadcast.sendStartedAt)
                                  : broadcast.scheduledAt
                                    ? formatDateTime(broadcast.scheduledAt)
                                    : formatRelativeTime(broadcast.createdAt)}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewDetail(broadcast);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {t('broadcasts.pageOf', { current: currentPage, total: totalPages })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="segments" className="mt-4">
          <AudienceSegmentsTab businessId={selectedBusinessId} />
        </TabsContent>
      </Tabs>

      {/* Create Campaign Dialog */}
      {selectedBusinessId && (
        <CreateCampaignDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          businessId={selectedBusinessId}
          onCreated={fetchBroadcasts}
        />
      )}

      {/* Campaign Detail Sheet */}
      <CampaignDetailSheet
        open={detailOpen}
        onOpenChange={setDetailOpen}
        broadcast={selectedBroadcast}
        onAction={fetchBroadcasts}
      />
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/broadcasts')({
  component: BroadcastsPage,
});
